import { useCallback } from "react";

export type Direction = "start" | "next" | "prev";

interface UseInfiniteScrollProps<T> {
  // The list of infinite scroll elements
  items: T[];
  scrolledToTopHandler?: () => void;
  scrolledToBottomHandler?: () => void;
}

interface UseInfiniteScrollReturn {
  topSentinelRef: (node: HTMLElement | null) => void;
  bottomSentinelRef: (node: HTMLElement | null) => void;
}

export function useInfiniteScroll<T>({
  items,
  scrolledToTopHandler,
  scrolledToBottomHandler
}: UseInfiniteScrollProps<T>): UseInfiniteScrollReturn {
  const topSentinelRef = useCallback(
    (node: HTMLElement | null) => {
      if (!node) return;
      const observer = new IntersectionObserver(
        (entries) => {
          // If the sentinel is visible because there are no items, don't call the handler
          if (items.length === 0) return;
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              scrolledToTopHandler?.();
            }
          });
        },
        { threshold: 0.2 }
      );
      observer.observe(node);
    },
    [items.length, scrolledToTopHandler]
  );

  const bottomSentinelRef = useCallback(
    (node: HTMLElement | null) => {
      if (!node) return;
      const observer = new IntersectionObserver(
        (entries) => {
          // If the sentinel is visible because there are no items, don't call the handler
          if (items.length === 0) return;
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              scrolledToBottomHandler?.();
            }
          });
        },
        { threshold: 0.2 }
      );
      observer.observe(node);
    },
    [items.length, scrolledToBottomHandler]
  );

  return { topSentinelRef, bottomSentinelRef };
}
